import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { OrgTypes } from '@mode/shared/contract-common';
import { LabeledMenuComponent, MenuItem } from '@mode/shared/ui';
import { partition } from 'lodash';
import { ColorPalettePreviewSwatchComponent } from '../color-palette-preview-swatch/color-palette-preview-swatch.component';

export interface ColorPaletteMenuItem extends MenuItem {
  name: string;
  palette?: OrgTypes.ColorPalette;
}

@Component({
  standalone: true,
  imports: [CommonModule, LabeledMenuComponent],
  selector: 'mode-color-palette-selector',
  templateUrl: './color-palette-selector.component.html',
  styleUrls: ['./color-palette-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorPaletteSelectorComponent implements OnChanges {
  @Input() selectedColorPalette: OrgTypes.ColorPalette | undefined = undefined;
  @Input() allColorPalettes: OrgTypes.ColorPalette[] = [];
  @Input() buttonHeight = 'normal';
  @Input() label = '';

  @Output() paletteChanged = new EventEmitter<OrgTypes.ColorPalette>();

  menuItems: ColorPaletteMenuItem[] = [];
  selectedItem: ColorPaletteMenuItem | undefined = undefined;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allColorPalettes']?.currentValue) {
      // Partition the list into custom and predefined
      const [customPalettes, predefinedPalettes] = partition(
        changes['allColorPalettes'].currentValue,
        (palette) => !palette.predefined
      );
      // Create the custom palettes menu items
      let paletteMenuItems = this.makePaletteSectionMenuItems('Custom Palettes', customPalettes);
      // Add in the predefined palettes menu items
      paletteMenuItems = paletteMenuItems.concat(
        this.makePaletteSectionMenuItems('Default Mode Palettes', predefinedPalettes)
      );
      this.menuItems = paletteMenuItems;
    }

    this.selectedItem = this.menuItems.find((item) => item.name === this.selectedColorPalette?.name);
  }

  onPaletteSelected(palette: ColorPaletteMenuItem) {
    this.selectedItem = palette;
    this.paletteChanged.emit(palette.palette);
  }

  private makePaletteSectionMenuItems(sectionName: string, palettes: OrgTypes.ColorPalette[]) {
    let paletteMenuItems: Array<ColorPaletteMenuItem> = [];
    // Only create the section if there are palettes to include
    if (palettes.length > 0) {
      // Add an item to display the section header
      paletteMenuItems.push({
        name: sectionName,
        section: true,
      });
      paletteMenuItems = paletteMenuItems.concat(
        palettes.map((palette: OrgTypes.ColorPalette) => {
          // Set up the inputs for the custom color palette preview swatch
          const inputs = new Map<string, unknown>();
          inputs.set('selectedColorPalette', palette);
          inputs.set('size', 'small');
          return {
            name: palette.name,
            palette,
            decorator: {
              component: ColorPalettePreviewSwatchComponent,
              inputs,
            },
          };
        })
      );
    }
    return paletteMenuItems;
  }
}
